import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Phone, FileText, CurrencyDollar, Clock, CheckCircle, XCircle } from '@phosphor-icons/react';
import api, { getErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { CallRecordingPlayer } from '../components/CallRecordingPlayer';

const NotificationsAbsent = () => {
  const { activeBranch } = useBranch();
  const branchId = activeBranch?.branch_id || '';
  
  // DEBUG: Log branch context
  console.log('[NotifCredits] Branch context:', { 
    activeBranch: activeBranch?.name, 
    branchId, 
    branch_id: activeBranch?.branch_id 
  });
  
  const [absentCustomers, setAbsentCustomers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [credits, setCredits] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [globalAutoCall, setGlobalAutoCall] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  // Call summary modal state
  const [summaryModal, setSummaryModal] = useState(null);
  const [summaryData, setSummaryData] = useState({
    customer_reached: false,
    reason: '',
    will_come_back: 'maybe',
    additional_notes: ''
  });
  
  // Recharge modal state
  const [rechargeModal, setRechargeModal] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(10);

  // Load Razorpay script dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      // Cleanup: remove script when component unmounts
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedDate, branchId]);

  const fetchData = async () => {
    try {
      const [absentRes, logsRes, creditsRes] = await Promise.all([
        api.get(`/notifications/absent-customers?date=${selectedDate}&branch_id=${branchId}`),
        api.get('/notifications/call-logs?limit=20'),
        branchId ? api.get(`/notification-credits/${branchId}`) : Promise.resolve({ data: { balance: 0, transactions: [] } })
      ]);
      
      setAbsentCustomers(absentRes.data);
      setCallLogs(logsRes.data);
      setCredits(creditsRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load data'));
    }
  };

  const handleAICall = async (customerId, customer) => {
    if (credits.balance < 1) {
      toast.error('Insufficient notification credits. Please recharge.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(`/notifications/manual-call/${customerId}`);
      const logId = response.data.log_id;
      
      // Validate log_id before opening modal
      if (!logId) {
        toast.error('Failed to create call log. Please try again.');
        console.error('No log_id returned from manual-call endpoint:', response.data);
        return;
      }
      
      toast.success('Call initiated! Please dial the customer and fill the summary after the call.');
      
      // Open summary modal for manual entry AFTER staff makes the actual call
      setTimeout(() => {
        setSummaryModal({
          log_id: logId,
          customer_name: customer.name,
          customer_id: customerId,
          ai_call: true
        });
      }, 1000);
      
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to initiate call'));
    } finally {
      setLoading(false);
    }
  };

  const handleNormalCall = async (customer) => {
    setLoading(true);
    try {
      // Create call log first for normal calls
      const response = await api.post(`/notifications/normal-call/${customer.customer_id}`);
      const logId = response.data.log_id;
      
      if (!logId) {
        toast.error('Failed to create call log. Please try again.');
        return;
      }
      
      // Open phone dialer
      window.location.href = `tel:${customer.phone_number}`;
      
      // Open summary modal after a short delay
      setTimeout(() => {
        setSummaryModal({
          log_id: logId,
          customer_name: customer.name,
          customer_id: customer.customer_id,
          ai_call: false
        });
      }, 1000);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create call log'));
    } finally {
      setLoading(false);
    }
  };

  const saveSummary = async () => {
    if (!summaryModal?.log_id) {
      toast.error('Invalid call log. Please close this window and try calling again.');
      setSummaryModal(null);
      return;
    }
    
    if (!summaryData.reason.trim()) {
      toast.error('Please provide a reason or notes about the call.');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/notifications/call-summary/${summaryModal.log_id}`, summaryData);
      toast.success('Call summary saved! Credit deducted and session note created.');
      setSummaryModal(null);
      setSummaryData({ customer_reached: false, reason: '', will_come_back: 'maybe', additional_notes: '' });
      fetchData();
    } catch (error) {
      const errorMsg = getErrorMessage(error, 'Failed to save summary');
      toast.error(errorMsg);
      
      // If call log not found, close the modal and prompt user to try again
      if (errorMsg.includes('Call log not found')) {
        toast.error('Please close this window and click AI Call button again to create a new call log.');
        setTimeout(() => setSummaryModal(null), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    if (rechargeAmount < 10) {
      toast.error('Minimum recharge is 10 credits');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Initiating recharge:', { branchId, credits: rechargeAmount });
      console.log('API URL:', `/notification-credits/${branchId}/recharge`);
      
      const orderRes = await api.post(`/notification-credits/${branchId}/recharge`, {
        branch_id: branchId,
        credits: rechargeAmount
      });
      
      console.log('Recharge response:', orderRes.data);
      
      const options = {
        key: orderRes.data.razorpay_key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        order_id: orderRes.data.order_id,
        name: 'CERACARE Notification Credits',
        description: `${rechargeAmount} notification credits (₹${(rechargeAmount * 15).toFixed(2)})`,
        handler: async function (response) {
          try {
            const verifyRes = await api.post(`/notification-credits/${branchId}/verify-payment`, {
              branch_id: branchId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              credits: rechargeAmount
            });
            
            const { credits_added, new_balance } = verifyRes.data;
            const amountPaid = credits_added * 15;
            
            toast.success(`₹${amountPaid} paid. ${credits_added} credits added. Balance: ${new_balance}`);
            setRechargeModal(false);
            fetchData();
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: 'Branch Owner',
          email: '',
          contact: ''
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Recharge error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(getErrorMessage(error, 'Failed to initiate recharge'));
    } finally {
      setLoading(false);
    }
  };

  // Group customers by category (as per user requirement)
  const groupedCustomers = {
    'New': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === 'New' || cat === 'New Customer';
    }),
    '10 Days': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === '10 Days';
    }),
    '30 Days': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === '30 Days';
    }),
    '60 Days': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === '60 Days';
    }),
    '90 Days': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === '90 Days';
    }),
    '120 Days': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === '120 Days';
    }),
    '120 Days Above': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === '120 Days Above' || cat === '120+ Days';
    }),
    'Buyers': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === 'Buyers' || cat === 'Buyer';
    }),
    'Challenge Therapy': absentCustomers.filter(c => {
      const cat = c.category || c.customer_category || '';
      return cat === 'Challenge Therapy';
    })
  };

  const categoryIcons = {
    'New': '✨',
    '10 Days': '📅',
    '30 Days': '📆',
    '60 Days': '🗓️',
    '90 Days': '📊',
    '120 Days': '📈',
    '120 Days Above': '⏰',
    'Buyers': '💎',
    'Challenge Therapy': '🎯'
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light text-[#1C1C1A] mb-2">Absent Customer Notifications</h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">AI-powered calls to customers who did not check in today</p>
          </div>
          
          {/* Credits and Recharge - Stack on mobile */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="text-left sm:text-right">
              <p className="text-xs sm:text-sm text-[#1C1C1A]/60">Notification Credits</p>
              <p className="text-xl sm:text-2xl font-semibold text-[#2C4C3B]">{credits.balance}</p>
            </div>
            <Button 
              onClick={() => setRechargeModal(true)} 
              className="bg-[#C05C42] hover:bg-[#C05C42]/90 text-sm sm:text-base whitespace-nowrap"
            >
              <CurrencyDollar className="w-4 h-4 mr-1 sm:mr-2" />
              Recharge
            </Button>
          </div>
        </div>

        {/* Date Selector and Global Auto Call Toggle - Mobile Responsive */}
        <Card className="p-4 bg-white border-[#E5E5DF]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Date Picker */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Label className="text-sm sm:text-base whitespace-nowrap">Date:</Label>
              <Input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full sm:w-48 text-sm sm:text-base"
              />
            </div>
            
            {/* Auto Calls Toggle */}
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-sm font-medium text-[#1C1C1A] whitespace-nowrap">🌐 Auto Calls:</span>
              <button
                onClick={() => setGlobalAutoCall(!globalAutoCall)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  globalAutoCall ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    globalAutoCall ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-[#1C1C1A]/70">
                {globalAutoCall ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>
          
          {globalAutoCall && (
            <p className="mt-2 text-sm text-green-700 bg-green-50 p-2 rounded">
              ✓ AI calling enabled - Staff can manually trigger AI calls for absent customers
            </p>
          )}
          {!globalAutoCall && (
            <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
              AI calling disabled - Staff will use normal phone calls
            </p>
          )}
        </Card>

        {/* Transaction History Section */}
        {credits.transactions && credits.transactions.length > 0 && (
          <Card className="p-4 bg-white border-[#E5E5DF]">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-[#1C1C1A]">
              <Clock size={20} />
              Credit Transaction History
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Action</th>
                    <th className="text-right p-2">Credits</th>
                    <th className="text-right p-2">Amount (₹)</th>
                    <th className="text-right p-2">Balance After</th>
                    <th className="text-left p-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.transactions.map((tx, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-2">{new Date(tx.date).toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit'})}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          tx.action === 'recharge' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {tx.action === 'recharge' ? '↑ Recharge' : '↓ AI Call Used'}
                        </span>
                      </td>
                      <td className={`p-2 text-right font-medium ${tx.action === 'recharge' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.action === 'recharge' ? `+${tx.credits_purchased}` : `-1`}
                      </td>
                      <td className="p-2 text-right">{tx.amount_paid ? `₹${tx.amount_paid}` : '-'}</td>
                      <td className="p-2 text-right font-medium">{tx.balance_after}</td>
                      <td className="p-2 text-gray-500 text-xs">{tx.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Absent Customers List - Grouped by Category */}
        <Card className="p-6 bg-white border-[#E5E5DF]">
          <h2 className="text-xl font-medium text-[#1C1C1A] mb-4">
            📋 Absent Today — {selectedDate}
          </h2>
          
          <div className="space-y-3">
            {Object.keys(groupedCustomers).map((category) => {
              const customers = groupedCustomers[category];
              if (customers.length === 0) return null;
              
              return (
                <div key={category} className="border border-[#E5E5DF] rounded-lg">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                    className="w-full flex items-center justify-between p-4 hover:bg-[#F9F9F8]"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{categoryIcons[category]}</span>
                      <span className="font-medium text-[#1C1C1A]">{category}</span>
                      <span className="text-sm text-[#1C1C1A]/60">({customers.length})</span>
                    </div>
                    <span className="text-[#1C1C1A]/40">
                      {expandedCategory === category ? '▼' : '▶'}
                    </span>
                  </button>
                  
                  {expandedCategory === category && (
                    <div className="border-t border-[#E5E5DF] p-4 space-y-3 bg-[#F9F9F8]">
                      {customers.map((customer) => (
                        <div
                          key={customer.customer_id}
                          className="border border-[#E5E5DF] rounded-lg p-4 bg-white"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-[#1C1C1A]">{customer.name}</p>
                              <p className="text-sm text-[#1C1C1A]/60">
                                Last Visit: {customer.last_visit} | Phone: {customer.phone_number}
                              </p>
                              
                              {customer.call_log && (
                                <div className="mt-2 flex items-center gap-2 text-sm">
                                  {customer.call_log.status === 'completed' ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : customer.call_log.status === 'no_answer' ? (
                                    <XCircle className="w-4 h-4 text-red-600" />
                                  ) : (
                                    <Clock className="w-4 h-4 text-yellow-600" />
                                  )}
                                  <span className="text-[#1C1C1A]/70">
                                    {customer.call_log.call_type === 'ai_call' ? 'AI Call' : 'Normal Call'}: {customer.call_log.status}
                                  </span>
                                  {customer.call_log.credits_used > 0 && (
                                    <span className="text-[#C05C42]">({customer.call_log.credits_used} credits)</span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {globalAutoCall ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleAICall(customer.customer_id, customer)}
                                  disabled={loading || customer.call_log}
                                  className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
                                >
                                  <Phone className="w-4 h-4 mr-1" />
                                  AI Call
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleNormalCall(customer)}
                                  disabled={loading}
                                  variant="outline"
                                >
                                  <Phone className="w-4 h-4 mr-1" />
                                  Call
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Recording: inline player, or attach button for staff-recorded calls */}
                          {customer.call_log && (
                            <CallRecordingPlayer log={customer.call_log} onChanged={fetchData} />
                          )}
                          
                          {customer.call_log?.transcript && (
                            <div className="mt-2">
                              <details className="text-sm">
                                <summary className="cursor-pointer text-[#2C4C3B] hover:underline flex items-center gap-1">
                                  <FileText className="w-4 h-4" />
                                  View Transcript
                                </summary>
                                <p className="mt-2 p-2 bg-[#F9F9F8] rounded text-[#1C1C1A]/80 whitespace-pre-wrap">
                                  {customer.call_log.transcript}
                                </p>
                              </details>
                            </div>
                          )}
                          
                          {customer.call_log?.call_summary && (
                            <div className="mt-2 p-2 bg-[#E2E8E4] rounded text-sm">
                              <p className="font-medium text-[#1C1C1A]">📝 Call Summary:</p>
                              <p className="text-[#1C1C1A]/80 whitespace-pre-wrap">{customer.call_log.call_summary}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            
            {absentCustomers.length === 0 && (
              <p className="text-center py-8 text-[#1C1C1A]/40">No absent customers</p>
            )}
          </div>
        </Card>

        {/* Call Summary Modal */}
        {summaryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="p-6 bg-white max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-[#1C1C1A] mb-4">
                Call Summary - {summaryModal.customer_name}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Did customer answer?</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={summaryData.customer_reached}
                        onChange={() => setSummaryData({...summaryData, customer_reached: true})}
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!summaryData.customer_reached}
                        onChange={() => setSummaryData({...summaryData, customer_reached: false})}
                      />
                      No
                    </label>
                  </div>
                </div>
                
                <div>
                  <Label>Reason</Label>
                  <Textarea
                    value={summaryData.reason}
                    onChange={(e) => setSummaryData({...summaryData, reason: e.target.value})}
                    placeholder="Why didn't they come?"
                  />
                </div>
                
                <div>
                  <Label>Will they visit next?</Label>
                  <select
                    value={summaryData.will_come_back}
                    onChange={(e) => setSummaryData({...summaryData, will_come_back: e.target.value})}
                    className="w-full border rounded p-2"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="maybe">Maybe</option>
                  </select>
                </div>
                
                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={summaryData.additional_notes}
                    onChange={(e) => setSummaryData({...summaryData, additional_notes: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={saveSummary}
                    disabled={loading}
                    className="flex-1 bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => setSummaryModal(null)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Recharge Modal */}
        {rechargeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="p-6 bg-white max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-[#1C1C1A] mb-4">
                Recharge Notification Credits
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Credits to Purchase (₹15/credit, min 10)</Label>
                  <Input
                    type="number"
                    min="10"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(parseInt(e.target.value) || 10)}
                  />
                  <p className="text-sm text-[#1C1C1A]/60 mt-1">
                    Total: ₹{(rechargeAmount * 15).toFixed(2)}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleRecharge}
                    disabled={loading || rechargeAmount < 10}
                    className="flex-1 bg-[#C05C42] hover:bg-[#C05C42]/90"
                  >
                    Pay ₹{(rechargeAmount * 15).toFixed(2)}
                  </Button>
                  <Button
                    onClick={() => setRechargeModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NotificationsAbsent;
